import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useSchoolDays } from '../../hooks/useSchoolDays';

export default function TeacherAttendance() {
  // ═══ School days hook — INSIDE the component (React rule) ═══
  const { isSchoolDay, getPreviousSchoolDay, getNextSchoolDay } = useSchoolDays();

  const [myClass, setMyClass] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [existingRecords, setExistingRecords] = useState({});
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // ===== DATE RESTRICTION =====
  const todayStr = new Date().toISOString().split('T')[0];
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const isToday = attendanceDate === todayStr;
  const isYesterday = attendanceDate === yesterdayStr;
  const isFuture = attendanceDate > todayStr;
  const isTooOld = attendanceDate < yesterdayStr;
  const isSchoolDaySelected = isSchoolDay(attendanceDate);

  // Editable only if: today OR yesterday AND it's a school day
  const isEditable = (isToday || isYesterday) && isSchoolDaySelected;

  useEffect(() => { fetchClass(); }, []);

  useEffect(() => {
    if (myClass) {
      fetchExisting();
      fetchHistory();
    }
  }, [attendanceDate, myClass]);

  const fetchClass = async () => {
    try {
      const res = await api.get('/teacher/my-class');
      setMyClass(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExisting = async () => {
    try {
      const res = await api.get(`/attendance/?class_id=${myClass.class_id}`);
      const map = {};
      const existing = {};
      res.data.forEach(r => {
        if (r.date === attendanceDate) {
          map[r.student_id] = r.status;
          existing[r.student_id] = r.id;
        }
      });
      setAttendanceMap(map);
      setExistingRecords(existing);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/attendance/?class_id=${myClass.class_id}`);
      setAllRecords(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const setStatus = (studentId, status) => {
    if (!isEditable) return;
    setAttendanceMap(prev => ({ ...prev, [studentId]: status }));
  };

  const markAllPresent = () => {
    if (!isEditable) return;
    const map = {};
    myClass.students.forEach(s => { map[s.student_id] = 'Present'; });
    setAttendanceMap(map);
  };

  const handleSave = async () => {
    if (!myClass || !isEditable) return;

    const records = Object.entries(attendanceMap).map(([studentId, status]) => ({
      student_id: parseInt(studentId),
      class_id: myClass.class_id,
      date: attendanceDate,
      status: status,
      remarks: null,
    }));

    if (records.length === 0) {
      alert('Please mark attendance for at least one student');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/attendance/bulk', records);
      alert(`✅ Saved! ${res.data.created} new, ${res.data.updated} updated`);
      fetchExisting();
      fetchHistory();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ===== DATE NAVIGATION (school-days-aware) =====
  const goPrevDay = () => {
    setAttendanceDate(getPreviousSchoolDay(attendanceDate));
  };

  const goNextDay = () => {
    const next = getNextSchoolDay(attendanceDate);
    if (next > todayStr) return;
    setAttendanceDate(next);
  };

  const goToday = () => {
    // If today isn't a school day, jump to most recent school day
    setAttendanceDate(isSchoolDay(todayStr) ? todayStr : getPreviousSchoolDay(todayStr));
  };

  const goYesterday = () => {
    setAttendanceDate(getPreviousSchoolDay(todayStr));
  };

  const goFirstOfMonth = () => {
    const d = new Date(attendanceDate);
    d.setDate(1);
    let first = d.toISOString().split('T')[0];
    // If first of month isn't a school day, jump forward
    let guard = 0;
    while (!isSchoolDay(first) && guard < 14) {
      const next = new Date(first);
      next.setDate(next.getDate() + 1);
      first = next.toISOString().split('T')[0];
      guard++;
    }
    if (first <= todayStr) setAttendanceDate(first);
  };

  // ===== HISTORY GROUPING =====
  const historyByDate = {};
  allRecords.forEach(r => {
    if (!historyByDate[r.date]) {
      historyByDate[r.date] = {
        date: r.date,
        records: [],
        present: 0,
        absent: 0,
        late: 0,
        total: 0,
      };
    }
    historyByDate[r.date].records.push(r);
    historyByDate[r.date].total++;
    if (r.status === 'Present') historyByDate[r.date].present++;
    else if (r.status === 'Absent') historyByDate[r.date].absent++;
    else if (r.status === 'Late') historyByDate[r.date].late++;
  });

  const sortedDates = Object.keys(historyByDate).sort((a, b) => b.localeCompare(a));

  const filteredDates = sortedDates.filter(d => {
    const day = historyByDate[d];
    if (filterStatus !== 'all') {
      if (filterStatus === 'Present' && day.present === 0) return false;
      if (filterStatus === 'Absent' && day.absent === 0) return false;
      if (filterStatus === 'Late' && day.late === 0) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = day.records.some(r =>
        (r.student_name || '').toLowerCase().includes(q)
      );
      if (!match) return false;
    }
    return true;
  });

  if (loading) return <div className="bg-white p-6 rounded shadow text-center">Loading...</div>;
  if (!myClass?.class_name) return <div className="bg-white p-6 rounded shadow text-center">No class assigned.</div>;

  const present = Object.values(attendanceMap).filter(s => s === 'Present').length;
  const absent = Object.values(attendanceMap).filter(s => s === 'Absent').length;
  const late = Object.values(attendanceMap).filter(s => s === 'Late').length;
  const unmarked = myClass.total_students - Object.keys(attendanceMap).length;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* HEADER */}
      <div className="bg-white p-5 rounded shadow mb-6">
        <div className="flex items-center gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📅 Mark Attendance</h1>
            <p className="text-sm text-gray-500">
              Class: {myClass.class_name} · {myClass.campus_name}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div>
                <input
                  type="month"
                  className="p-2 border rounded text-sm"
                  value={attendanceDate.substring(0, 7)}
                  max={todayStr.substring(0, 7)}
                  onChange={e => {
                    if (!e.target.value) return;
                    const currentDay = attendanceDate.substring(8);
                    const newDate = `${e.target.value}-${currentDay}`;
                    if (newDate <= todayStr) {
                      setAttendanceDate(newDate);
                    } else {
                      setAttendanceDate(`${e.target.value}-01`);
                    }
                  }}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Jump to month</p>
              </div>
              <div>
                <input
                  type="date"
                  className="p-2 border rounded"
                  value={attendanceDate}
                  max={todayStr}
                  onChange={e => setAttendanceDate(e.target.value)}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Pick any past date to view
                </p>
              </div>
            </div>
            <button
              onClick={markAllPresent}
              disabled={!isEditable}
              className="px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-check-double mr-1"></i> All Present
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !isEditable}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : <><i className="fas fa-save mr-1"></i> Save</>}
            </button>
          </div>
        </div>

        {/* DATE NAVIGATION */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <button
            onClick={goPrevDay}
            className="px-4 py-2 text-sm bg-white hover:bg-gray-100 rounded-md text-gray-700 font-medium border shadow-sm"
            title="Previous school day"
          >
            <i className="fas fa-chevron-left mr-1"></i> Prev
          </button>

          <div className="text-center">
            <p className="text-base font-bold text-gray-800">
              {formatDate(attendanceDate)}
            </p>
            <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
              {isToday && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  Today
                </span>
              )}
              {isYesterday && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                  Yesterday · Editable
                </span>
              )}
              {isTooOld && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                  🔒 Locked · View Only
                </span>
              )}
              {!isSchoolDaySelected && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  ⚠️ Non-school day
                </span>
              )}
            </div>

            {/* Quick jump shortcuts */}
            <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
              {!isToday && (
                <button
                  onClick={goToday}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 font-semibold border border-blue-200"
                >
                  Today
                </button>
              )}
              {!isYesterday && (
                <button
                  onClick={goYesterday}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 hover:bg-orange-100 font-semibold border border-orange-200"
                >
                  Yesterday
                </button>
              )}
              <button
                onClick={goFirstOfMonth}
                className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 font-semibold border border-gray-200"
              >
                First of Month
              </button>
            </div>
          </div>

          <button
            onClick={goNextDay}
            disabled={attendanceDate >= todayStr}
            className="px-4 py-2 text-sm bg-white hover:bg-gray-100 rounded-md text-gray-700 font-medium border shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            title="Next school day"
          >
            Next <i className="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>

      {/* ═══ NON-SCHOOL DAY WARNING ═══ */}
      {!isSchoolDaySelected && (
        <div className="p-3 rounded mb-6 border-l-4 bg-amber-50 border-amber-500 text-amber-800">
          <p className="text-sm font-medium flex items-center gap-2">
            <i className="fas fa-exclamation-triangle"></i>
            This is a non-school day. Attendance cannot be marked.
            You can still view the historical records below.
          </p>
        </div>
      )}

      {/* LOCK WARNING — FUTURE */}
      {!isEditable && !isTooOld && isFuture && (
        <div className="p-3 rounded mb-6 border-l-4 bg-yellow-50 border-yellow-500 text-yellow-800">
          <p className="text-sm font-medium flex items-center gap-2">
            <i className="fas fa-clock"></i>
            Cannot mark attendance for a future date.
          </p>
        </div>
      )}

      {/* LOCK WARNING — TOO OLD */}
      {isTooOld && (
        <div className="p-3 rounded mb-6 border-l-4 bg-gray-100 border-gray-400 text-gray-700">
          <p className="text-sm font-medium flex items-center gap-2">
            <i className="fas fa-lock"></i>
            This is a historical record. Attendance for dates older than yesterday is view-only.
          </p>
        </div>
      )}

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-green-600">Present</p>
          <p className="text-2xl font-bold text-green-700">{present}</p>
        </div>
        <div className="bg-red-50 p-4 rounded shadow border-l-4 border-red-500">
          <p className="text-xs text-red-600">Absent</p>
          <p className="text-2xl font-bold text-red-700">{absent}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-yellow-600">Late</p>
          <p className="text-2xl font-bold text-yellow-700">{late}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded shadow border-l-4 border-gray-500">
          <p className="text-xs text-gray-600">Unmarked</p>
          <p className="text-2xl font-bold text-gray-700">{unmarked}</p>
        </div>
      </div>

      {/* STUDENT LIST */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-3">
          👨‍🎓 Students
          <span className="text-xs font-normal text-gray-500 ml-2">
            ({myClass.total_students} total)
          </span>
        </h2>
        <div className="space-y-2">
          {myClass.students.map(s => {
            const status = attendanceMap[s.student_id];
            const alreadyMarked = existingRecords[s.student_id];
            return (
              <div
                key={s.student_id}
                className={`flex items-center justify-between p-3 rounded border ${
                  alreadyMarked ? 'bg-blue-50 border-blue-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                    {s.first_name[0]}
                  </div>
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-gray-500">
                      {s.roll_no ? `Roll #${s.roll_no}` : 'Roll not assigned'} · Reg: {s.registration_number}
                      {alreadyMarked && ' · ✓ Marked'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {['Present', 'Absent', 'Late'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setStatus(s.student_id, opt)}
                      disabled={!isEditable}
                      className={`px-4 py-2 rounded text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        status === opt
                          ? opt === 'Present'
                            ? 'bg-green-600 text-white'
                            : opt === 'Absent'
                              ? 'bg-red-600 text-white'
                              : 'bg-yellow-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== ATTENDANCE HISTORY ===== */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold">
            📜 Attendance History
            <span className="text-xs font-normal text-gray-500 ml-2">
              ({sortedDates.length} day{sortedDates.length !== 1 ? 's' : ''} recorded)
            </span>
          </h2>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 hover:underline font-medium"
          >
            {showHistory ? '▲ Hide' : '▼ Show History'}
          </button>
        </div>

        {showHistory && (
          <>
            {/* HISTORY FILTERS */}
            <div className="bg-gray-50 rounded p-3 mb-4 flex flex-wrap gap-2 items-center">
              <label className="text-xs font-semibold text-gray-600">Filter:</label>
              <select
                className="p-2 border rounded text-sm bg-white"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Present">Days with Present</option>
                <option value="Absent">Days with Absent</option>
                <option value="Late">Days with Late</option>
              </select>

              <input
                type="text"
                placeholder="Search student name..."
                className="flex-1 min-w-[180px] p-2 border rounded text-sm bg-white"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />

              {(filterStatus !== 'all' || searchQuery) && (
                <button
                  onClick={() => { setFilterStatus('all'); setSearchQuery(''); }}
                  className="text-xs text-blue-600 hover:underline font-medium"
                >
                  Clear
                </button>
              )}

              <span className="text-xs text-gray-500 ml-auto">
                Showing {filteredDates.length} of {sortedDates.length}
              </span>
            </div>

            {/* HISTORY LIST */}
            {filteredDates.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="fas fa-inbox text-4xl mb-3"></i>
                <p>No matching attendance records.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDates.map(dateStr => {
                  const day = historyByDate[dateStr];
                  const isSelected = dateStr === attendanceDate;
                  const isTodayRow = dateStr === todayStr;
                  const isYesterdayRow = dateStr === yesterdayStr;

                  return (
                    <div
                      key={dateStr}
                      className={`rounded-lg border-2 overflow-hidden ${
                        isSelected ? 'border-blue-500 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      <div
                        onClick={() => setAttendanceDate(dateStr)}
                        className={`p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <i className={`fas fa-calendar-day ${
                            isTodayRow ? 'text-blue-500'
                            : isYesterdayRow ? 'text-orange-500'
                            : 'text-gray-400'
                          }`}></i>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">
                              {formatDate(dateStr)}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {isTodayRow && 'Today'}
                              {isYesterdayRow && 'Yesterday'}
                              {!isTodayRow && !isYesterdayRow && (
                                isSelected ? 'Selected' : 'Click to view'
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-xs font-semibold">
                          <span className="px-2 py-1 rounded bg-green-100 text-green-800">
                            P: {day.present}
                          </span>
                          <span className="px-2 py-1 rounded bg-red-100 text-red-800">
                            A: {day.absent}
                          </span>
                          <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                            L: {day.late}
                          </span>
                          <span className="text-gray-400">/ {day.total}</span>
                        </div>
                      </div>

                      <div className="border-t border-gray-100 max-h-64 overflow-y-auto">
                        {day.records
                          .filter(r => {
                            if (filterStatus === 'all') return true;
                            return r.status === filterStatus;
                          })
                          .filter(r => {
                            if (!searchQuery) return true;
                            return (r.student_name || '').toLowerCase().includes(searchQuery.toLowerCase());
                          })
                          .map(r => (
                            <div
                              key={r.id}
                              className="px-3 py-2 flex items-center justify-between text-sm border-b border-gray-50 last:border-b-0"
                            >
                              <span className="text-gray-700">{r.student_name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                r.status === 'Present' ? 'bg-green-100 text-green-800'
                                : r.status === 'Absent' ? 'bg-red-100 text-red-800'
                                : r.status === 'Late' ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-700'
                              }`}>
                                {r.status}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}